import {
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
  Input,
  Spacer,
} from '@nextui-org/react';
import { Divider } from 'antd';
import { Package } from '../../data/types/shipping';
import { all_packages_list } from '../../pages/create_listings/constants';
import { useState } from 'react';
import { IoChevronDownSharp } from 'react-icons/io5';
import { toast } from 'react-toastify';

export const PackageCard = ({
  packageData,
  setPackageData,
  removePackage,
  addPackage,
  customCardFooter,
  customCardClasses,
}: {
  packageData?: Package;
  setPackageData?: (pkg: Package, id: string) => void;
  removePackage?: (pkg: Package) => void;
  addPackage?: (pkg: Package) => void;
  customCardFooter?: JSX.Element;
  customCardClasses?: string;
}) => {
  const addingNew = !packageData;
  const [editing, setEditing] = useState(false && !addingNew);
  const [pkg, setPkg] = useState(
    packageData || {
      tracking_code: '',
      weight: 0,
      width: 0,
      height: 0,
      length: 0,
    },
  );
  const [selectedPackage, setSelectedPackage] = useState<
    | {
        name: string;
        width: number;
        height: number;
        length: number;
        image: JSX.Element | null;
      }
    | undefined
  >(undefined);
  const iconClasses =
    'text-xl text-default-500 pointer-events-none flex-shrink-0';

  function isFormValid() {
    // Check values aren't empty && All values are numbers && All values are positive && make approriate toast messages
    if (!pkg.tracking_code) {
      toast.warning('Please enter a tracking code');
      return false;
    }
    if (isNaN(pkg.weight)) {
      toast.warning('Please enter a valid weight');
      return false;
    }
    if (isNaN(pkg.width) || isNaN(pkg.height) || isNaN(pkg.length)) {
      toast.warning('Please enter valid dimensions');
      return false;
    }
    if (pkg.weight <= 0) {
      toast.warning('Weight must be greater than 0');
      return false;
    }
    if (pkg.width <= 0 || pkg.height <= 0 || pkg.length <= 0) {
      toast.warning('Dimensions must be greater than 0');
      return false;
    }
    return true;
  }
  return (
    <Card
      className={customCardClasses || 'w-64 h-96 m-2 mb-4'}
      shadow="sm"
      style={{
        display: 'flex',
        alignItems: 'center',
        flex: '0 0 auto',
      }}
    >
      <CardHeader className="flex gap-3">
        <div className="flex flex-col">
          <p className="text-md">Package #{pkg.tracking_code}</p>
        </div>
      </CardHeader>
      <Divider className="my-0" />
      <CardBody>
        <Input
          isDisabled={!editing && !addingNew}
          size="sm"
          label="Tracking Code"
          labelPlacement="outside-left"
          value={pkg.tracking_code}
          onChange={(e) => {
            setPkg({ ...pkg, tracking_code: e.target.value });
          }}
        />
        <Dropdown isDisabled={!editing && !addingNew}>
          <DropdownTrigger className="my-3">
            <Button
              variant="bordered"
              endContent={<IoChevronDownSharp className={iconClasses} />}
            >
              {!selectedPackage ? 'Package Dimensions' : selectedPackage.name}
            </Button>
          </DropdownTrigger>
          <DropdownMenu variant="faded" aria-label="Dropdown menu with icons">
            {all_packages_list.map((p, i) => (
              <DropdownItem
                key={i}
                startContent={
                  <div
                    className="flex items-center gap-2"
                    style={{
                      width: '20px',
                      height: '20px',
                    }}
                  >
                    {p.image}
                  </div>
                }
                onClick={() => {
                  setPkg({
                    ...pkg,
                    width: p.width,
                    height: p.height,
                    weight: p.weight,
                    length: p.length,
                  });
                  setSelectedPackage(p);
                }}
              >
                {p.name} - {p.width}x{p.height}x{p.length} cm
              </DropdownItem>
            ))}
          </DropdownMenu>
        </Dropdown>
        <Divider plain className="my-0">
          Or
        </Divider>
        <Input
          isDisabled={!editing && !addingNew}
          label="Weight (g)"
          labelPlacement="outside-left"
          className="my-2"
          value={pkg.weight.toString()}
          onChange={(e) => {
            if (isNaN(parseFloat(e.target.value))) {
              toast.warning('Please enter a valid number');
              return;
            }
            setPkg({ ...pkg, weight: parseFloat(e.target.value) });
          }}
        />
        <Spacer />
        <p
          style={{
            color: 'gray',
          }}
        >
          Length - Widht - Height (cm)
        </p>
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            gap: 'var(--lg)',
            justifyContent: 'flex-start',
            maxWidth: '250px',
          }}
        >
          <Input
            size="sm"
            isDisabled={!editing && !addingNew}
            className="my-2"
            value={pkg.length.toString()}
            onChange={(e) => {
              if (isNaN(parseFloat(e.target.value))) {
                toast.warning('Please enter a valid number');
                return;
              }
              setPkg({ ...pkg, length: parseFloat(e.target.value) });
            }}
          />
          <Input
            size="sm"
            isDisabled={!editing && !addingNew}
            className="my-2 mx-2"
            value={pkg.width.toString()}
            onChange={(e) => {
              if (isNaN(parseFloat(e.target.value))) {
                toast.warning('Please enter a valid number');
                return;
              }
              setPkg({ ...pkg, width: parseFloat(e.target.value) });
            }}
          />{' '}
          <Input
            size="sm"
            isDisabled={!editing && !addingNew}
            className="my-2"
            value={pkg.height.toString()}
            onChange={(e) => {
              if (isNaN(parseFloat(e.target.value))) {
                toast.warning('Please enter a valid number');
                return;
              }
              setPkg({ ...pkg, height: parseFloat(e.target.value) });
            }}
          />
        </div>
      </CardBody>
      <Divider className="my-0" />
      {customCardFooter || (
        <CardFooter
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          {editing ? (
            <>
              {removePackage !== undefined && (
                <Button
                  variant="ghost"
                  color="danger"
                  style={{
                    width: '48%',
                  }}
                  onClick={() => {
                    removePackage(pkg);
                    setEditing(false);
                  }}
                >
                  Delete
                </Button>
              )}
              <Button
                variant="solid"
                style={{
                  width: '48%',
                }}
                color="success"
                onClick={() => {
                  packageData &&
                    setPackageData &&
                    setPackageData(pkg, packageData.tracking_code);
                  setEditing(false);
                }}
              >
                Done
              </Button>
            </>
          ) : addPackage ? (
            <Button
              style={{
                width: '100%',
              }}
              variant="light"
              color="success"
              onClick={() => {
                if (!isFormValid()) return;
                addPackage(pkg);
                setPkg({
                  tracking_code: '',
                  weight: 0,
                  width: 0,
                  height: 0,
                  length: 0,
                });
              }}
            >
              Add
            </Button>
          ) : (
            <Button
              style={{
                width: '100%',
              }}
              variant="light"
              color="warning"
              onClick={() => {
                setEditing(true);
              }}
            >
              Edit
            </Button>
          )}
        </CardFooter>
      )}
    </Card>
  );
};
